import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import {
  generateNoscriptTableHtml,
  getTenantProfilesSections
} from '../../utils/seoCharts';

import { useTenantProfileStatisticsContext } from '../../context/TenantProfileStatisticsContext';

import { OCCUPATION, NATIONALITY, AGE } from '../../constants/seoCharts';

import ChartContainer from './ChartContainer';

import Table from './Table';
import HorizontalBarChart from './HorizontalBarChart';

const TABLE_LABELS_KEY = {
  [OCCUPATION]: ['chart.section.occupation', 'chart.table.percentage'],
  [NATIONALITY]: ['chart.section.nationality', 'chart.table.percentage'],
  [AGE]: ['chart.section.age', 'chart.table.percentage']
};

const TenantProfileChartContainer = ({ location, isMobile = false }) => {
  const sections = getTenantProfilesSections();
  const {
    tenantProfileStatistics,
    section,
    setSection,
    topAgeRange,
    topOccupation,
    topCountry
  } = useTenantProfileStatisticsContext();

  const handleTabChange = tabId => {
    const newSection = sections[tabId].id;
    setSection(newSection);
  };

  const activeIndex = section
    ? sections.findIndex(item => item.id === section)
    : 0;

  if (tenantProfileStatistics.length === 0) {
    return null;
  }

  const title = trans('chart.tenant.profile.title', { city: location.city });

  const chartAriaLabel = `${title}: ${tenantProfileStatistics.map(s => `${s.label} ${s.percentage}%`).join(', ')}`;

  const noscriptContent =
    tenantProfileStatistics.length > 0
      ? generateNoscriptTableHtml({
          title,
          headers: [
            trans(TABLE_LABELS_KEY[section][0]),
            trans('chart.table.percentage')
          ],
          rows: tenantProfileStatistics.map(s => [s.label, `${s.percentage}%`, s.delta])
        })
      : '';

  return (
    <ChartContainer
      id="tenant-profiles-chart"
      location={location}
      isMobile={isMobile}
      title={title}
      chartAriaLabel={chartAriaLabel}
      noscriptContent={noscriptContent}
      subtitle={trans('chart.tenant.profile.subtitle.plain', {
        city: location.city,
        country: trans(`country.${location.country}`),
        ageRange: topAgeRange
      })}
      summary={trans('chart.tenant.profile.summary.plain', {
        city: location.city,
        country: topCountry,
        ageRange: topAgeRange,
        occupation: topOccupation
      })}
      loading={false}
      onTabChange={handleTabChange}
      sections={sections}
      activeIndex={activeIndex}
      renderChart={() => <HorizontalBarChart data={tenantProfileStatistics} />}
      renderTable={() => (
        <Table
          data={tenantProfileStatistics}
          valueKey="percentage"
          labelKeys={TABLE_LABELS_KEY[section]}
          formatValue={value => `${value}%`}
        />
      )}
    />
  );
};

export default TenantProfileChartContainer;

TenantProfileChartContainer.propTypes = {
  isMobile: PropTypes.bool,
  location: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string
  }).isRequired
};
